import type { AccessLog, AppBranding, AuditEvent, RoleCapability, UserTeam, WorkspaceSettings } from "../app/types";

export type TeamMemberRecord = {
  name: string;
  role: string;
  access: string;
  allowedCategoryIds?: string[] | null;
  teamIds?: string[];
  initials: string;
  status: "Active" | "Pending";
  email: string;
};

export type RolePresetRecord = {
  role: string;
  detail: string;
  seats: string;
  capabilities: RoleCapability[];
};

export type IntegrationRecord = {
  id: string;
  name: string;
  status: string;
  detail: string;
};

export type AuditEventRecord = AuditEvent;

export type KnowledgeCollectionRecord = {
  id: string;
  name: string;
  detail: string;
  articleCount: number;
  sortOrder: number;
};

export type KnowledgeArticleRecord = {
  id: string;
  title: string;
  summary: string;
  meta: string;
  collectionId: string | null;
  visibility?: "workspace" | "private";
  ownerUserEmail?: string | null;
  sharedWithUserEmails?: string[];
  isFeatured: boolean;
  isPopular: boolean;
  sortOrder: number;
};

export type UserTeamRecord = Omit<UserTeam, "createdAt" | "updatedAt">;

export const defaultBranding: AppBranding = {
  primaryColor: "#006949",
  consoleName: null,
  logoDataUrl: null,
  logoName: null,
};

export const defaultWorkspaceSettings: WorkspaceSettings = {
  workspaceName: "",
  portalUrl: "",
  supportInbox: "",
  billingContact: "",
  timezone: "UTC",
  currency: "USD ($)",
  logTextLanguage: "en",
  caseEmailRoutes: [],
  notifications: {
    timezone: null,
    ticketCreated: {
      enabled: false,
      categoryIds: [],
    },
    ticketUpdated: {
      enabled: false,
      categoryIds: [],
    },
    ticketScheduledReminder: {
      enabled: false,
      categoryIds: [],
    },
    ticketSlaWarning: {
      enabled: false,
      categoryIds: [],
    },
    ticketSlaBreached: {
      enabled: false,
      categoryIds: [],
    },
  },
  localPasswordResetEnabled: true,
  apiAccessEnabled: false,
  apiKeyExpiryDays: null,
  auditLogsEnabled: true,
  auditLogRetentionDays: null,
  accessLogsEnabled: true,
  accessLogRetentionDays: null,
  archiveRetentionMonths: null,
  publicAttachmentDownloadsEnabled: false,
  attachmentDownloadOverridesEnabled: false,
  billingPlan: "growth",
  planLimits: null,
  welcomeShown: false,
};

export const userTeams: UserTeamRecord[] = [];

export const teamMembers: TeamMemberRecord[] = [];

export const rolePresets: RolePresetRecord[] = [
  {
    role: "Owner",
    detail: "Full access to workspace settings, team permissions, branding, integrations, billing, and operational data.",
    seats: "2 seats",
    capabilities: ["tickets:view", "tickets:edit", "tickets:delete", "customer:view", "customers:edit", "customers:delete", "kb:view", "kb:create", "kb:manage", "users:view", "users:manage", "notifications:view", "archive:view", "archive:edit", "settings:view", "settings:manage", "calendar:view", "calendar:create", "calendar:manage", "api:access", "billing:manage", "items:view", "items:manage", "expenses:view", "expenses:edit", "expenses:delete", "vendors:view", "vendors:edit", "vendors:delete", "reporting:view", "reporting:manage"],
  },
  {
    role: "Admin",
    detail: "Runs the workspace day to day, including users, categories, ticket operations, CRM updates, and knowledge management.",
    seats: "5 seats",
    capabilities: ["tickets:view", "tickets:edit", "tickets:delete", "customer:view", "customers:edit", "customers:delete", "kb:view", "kb:create", "kb:manage", "users:view", "users:manage", "notifications:view", "archive:view", "archive:edit", "settings:view", "settings:manage", "calendar:view", "calendar:create", "calendar:manage", "api:access", "billing:manage", "items:view", "items:manage", "expenses:view", "expenses:edit", "expenses:delete", "vendors:view", "vendors:edit", "vendors:delete", "reporting:view", "reporting:manage"],
  },
  {
    role: "Agent",
    detail: "Works tickets, reads customer context, and creates knowledge articles inside the categories assigned by an admin.",
    seats: "0 seats",
    capabilities: ["tickets:view", "tickets:edit", "customer:view", "kb:view", "kb:create", "calendar:view", "calendar:create"],
  },
  {
    role: "Customer manager",
    detail: "Updates customer accounts, contacts, products, and notes without changing ticket workflows.",
    seats: "0 seats",
    capabilities: ["customer:view", "customers:edit", "items:view"],
  },
  {
    role: "Knowledge manager",
    detail: "Maintains collections and published content for the knowledge base without managing users or customer records.",
    seats: "0 seats",
    capabilities: ["kb:view", "kb:create", "kb:manage"],
  },
  {
    role: "Reporting manager",
    detail: "Builds, runs, exports, and schedules reports without managing users, tickets, or customer records.",
    seats: "0 seats",
    capabilities: ["reporting:view", "reporting:manage"],
  },
  {
    role: "Viewer",
    detail: "Read-only access across tickets, customers, and knowledge content, with visibility into reporting dashboards.",
    seats: "4 seats",
    capabilities: ["tickets:view", "customer:view", "kb:view", "calendar:view", "items:view", "reporting:view"],
  },
];

export const integrations: IntegrationRecord[] = [];

export const auditEvents: AuditEventRecord[] = [];

export const accessLogs: AccessLog[] = [];

export const knowledgeCollections: KnowledgeCollectionRecord[] = [];

export const knowledgeArticles: KnowledgeArticleRecord[] = [];
